import { useMemo, useState } from "react";
import type { Product } from "@/interfaces/product";
import type { ProductFilterLabels } from "@/types/types";
import {
  availableCauses,
  availableGiftCategories,
  giftTypeFromCategory,
  hasAnyGiftCategory,
  shipsToCountry,
  supportsAnyCause,
} from "@/utils/redemption-helper";

type GiftType = "physical" | "charity" | "experiences";

// Left display-type chip label ↔ the coarse gift type giftTypeFromCategory buckets into.
const TYPE_BY_LABEL: Record<Exclude<ProductFilterLabels, "all">, GiftType> = {
  "Physical Gifts": "physical",
  "Charity Gifts": "charity",
  Experiences: "experiences",
};
const LABEL_BY_TYPE: Record<GiftType, ProductFilterLabels> = {
  physical: "Physical Gifts",
  charity: "Charity Gifts",
  experiences: "Experiences",
};

/**
 * Product-grid filtering shared by the collection detail views (SingleCollection + CollectionTier).
 * Mirrors the recipient feed's model (RedemptionCollection): Category and Cause are MULTI-select
 * (OR within each; empty = all), scoped to the active type. The left display-type chips stay a
 * single-select coarse filter layered on top — changing the type drops any category/cause the new
 * type has no gift for, so the grid never lands empty. Country filters physical (shipped) gifts
 * only; charity/experience gifts are country-agnostic. Returns the filtered list + a `barProps`
 * bag for <ProductFilterBar />.
 */
export function useProductFilters(products: Product[], activeCountry: string) {
  const [displayFilter, setDisplayFilter] = useState<ProductFilterLabels>("all");
  // GFG gift categories currently picked (multi-select); empty = all.
  const [activeCats, setActiveCats] = useState<string[]>([]);
  const [activeCauses, setActiveCauses] = useState<string[]>([]);

  // Category / Cause options are scoped to the active type, so a pick never empties the grid.
  const typeScoped = useMemo(
    () =>
      displayFilter === "all"
        ? products
        : products.filter((p) => giftTypeFromCategory(p.category) === TYPE_BY_LABEL[displayFilter]),
    [products, displayFilter],
  );
  const categories = useMemo(() => availableGiftCategories(typeScoped), [typeScoped]);
  const causes = useMemo(() => availableCauses(typeScoped), [typeScoped]);

  const filtered = useMemo(
    () =>
      products.filter((p) => {
        const typeOk =
          displayFilter === "all" ||
          giftTypeFromCategory(p.category) === TYPE_BY_LABEL[displayFilter];
        // Category/Cause are OR within each: empty = any, else match at least one pick, in any
        // position of the product's categories / causes.
        const catOk = activeCats.length === 0 || hasAnyGiftCategory(p, activeCats);
        const causeOk = activeCauses.length === 0 || supportsAnyCause(p, activeCauses);
        // Country applies to physical (shipped) gifts only; a physical gift with an empty
        // shippableCountries (no Shopify data) is hidden, not treated as unrestricted.
        const countryOk = shipsToCountry(p, activeCountry);
        return typeOk && catOk && causeOk && countryOk;
      }),
    [products, displayFilter, activeCats, activeCauses, activeCountry],
  );

  // ── Sync (mirrors RedemptionCollection.chooseType / chooseCat) ──
  const onDisplayFilterChange = (value: ProductFilterLabels) => {
    setDisplayFilter(value);
    if (value === "all") return;
    // Drop any category / cause pick the new type has no gift for, so we never land on an empty grid.
    const nextTypeProducts = products.filter(
      (p) => LABEL_BY_TYPE[giftTypeFromCategory(p.category)] === value,
    );
    setActiveCats((prev) =>
      prev.filter((c) => nextTypeProducts.some((p) => hasAnyGiftCategory(p, [c]))),
    );
    setActiveCauses((prev) =>
      prev.filter((cause) => nextTypeProducts.some((p) => supportsAnyCause(p, [cause]))),
    );
  };
  // Multi-select toggles: add the pick if new, remove it if already on (empty list = "all").
  const onChooseCat = (raw: string) =>
    setActiveCats((prev) => (prev.includes(raw) ? prev.filter((c) => c !== raw) : [...prev, raw]));
  const onChooseCause = (value: string) =>
    setActiveCauses((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value],
    );
  const onClearCats = () => setActiveCats([]);
  const onClearCauses = () => setActiveCauses([]);

  return {
    filtered,
    // Spread straight into <ProductFilterBar {...barProps} />.
    barProps: {
      displayFilter,
      onDisplayFilterChange,
      categories,
      activeCats,
      onChooseCat,
      onClearCats,
      causes,
      activeCauses,
      onChooseCause,
      onClearCauses,
    },
  };
}
